import type { AdminUser, DepartmentDetails } from '~/api/types';

import { adminUserFactory } from './factories/admin';
import { departmentDetailsFactory } from './factories/department';
import { IDNFactory } from './factories/IDN';
import { patientFactory } from './factories/patient';
import {
  REAL_DEPARTMENT_IDS,
  REAL_DEPARTMENT_NAMES,
  REAL_IDN_IDS,
  REAL_IDN_NAMES,
  REAL_USERS,
} from '../constants';
import { STORAGE_KEYS } from '../storageKeys';
import { getPersistSessionObject } from '../utils';

export const getFakeIDNData = () =>
  getPersistSessionObject(STORAGE_KEYS.IDNS, [
    ...Array.from({ length: 240 }, IDNFactory),
    // DON'T REMOVE: Ensure the presence of a real IDN for testing purposes until we remove all mocking data
    ...REAL_IDN_IDS.map((id, index) =>
      IDNFactory({
        id,
        name: REAL_IDN_NAMES[index],
      }),
    ),
  ]);

export const getFakeDepartmentData = () => {
  const idns = getFakeIDNData();

  return getPersistSessionObject<DepartmentDetails[]>(
    STORAGE_KEYS.DEPARTMENTS,
    [
      ...Array.from({ length: 240 }, () => departmentDetailsFactory({ idns })),
      // DON'T REMOVE: Ensure the presence of a real Department for testing purposes until we remove all mocking data
      ...REAL_DEPARTMENT_IDS.map((id, index) =>
        departmentDetailsFactory({
          idns,
          overrides: { id, name: REAL_DEPARTMENT_NAMES[index] },
        }),
      ),
    ],
  );
};

export const getFakePatientData = () => {
  const departmentData = getFakeDepartmentData();

  return getPersistSessionObject(
    STORAGE_KEYS.PATIENTS,
    Array.from({ length: 1160 }, () =>
      patientFactory({ departmentDetails: departmentData }),
    ),
  );
};

export const getFakeAdminUsersData = (): AdminUser[] => {
  const departmentData = getFakeDepartmentData();

  const fakeUsers = Array.from({ length: 240 }, () =>
    adminUserFactory({ departmentDetails: departmentData }),
  );

  const realUsersWithDepartments = REAL_USERS.map((user) =>
    adminUserFactory({
      departmentDetails: departmentData,
      overrides: {
        ...user,
        mainDepartment: departmentData.find((d) => d.id === user.departmentId),
      },
    }),
  );

  return getPersistSessionObject<AdminUser[]>(STORAGE_KEYS.ADMIN_USERS, [
    ...fakeUsers,
    ...realUsersWithDepartments,
  ]);
};
